import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ListTodo, Loader2 } from 'lucide-react';
import { Plus } from 'lucide-react';
import { AppointmentTypeCard } from './AppointmentTypeCard';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';
import bookingService, { AppointmentType } from '@/services/bookingService';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import AppointmentTypeDialog from '../dialog/AppoinmentTypeDialog';

const AppointmentTypeTab = () => {
  const { clinic } = useClinic();
  const { user } = useAuth();
  const { toast } = useToast();
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [v2UrlByType, setV2UrlByType] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [appointmentTypeDialogOpen, setAppointmentTypeDialogOpen] = useState(false);
  const [editingAppointmentType, setEditingAppointmentType] = useState<AppointmentType | null>(
    null
  );
  const fetchAppointmentTypes = async () => {
    try {
      setLoading(true);
      if (!clinic?.id) return;
      const [types, v2Links] = await Promise.all([
        bookingService.getAppointmentTypes(clinic.id, user?.practitionerId, false),
        bookingService.getV2BookingLinks(clinic.id).catch(() => null),
      ]);
      setAppointmentTypes(types);
      const map: Record<string, string> = {};
      v2Links?.appointment_types?.forEach((t) => {
        // Prefer the practitioner-scoped URL so the shareable link skips
        // the provider chooser for practitioner users.
        map[t.id] = t.practitioner_url || t.url;
      });
      setV2UrlByType(map);
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch appointment types',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointmentType = () => {
    setAppointmentTypeDialogOpen(true);
  };

  // Open edit appointment type dialog
  const handleEditAppointmentType = (type: AppointmentType) => {
    setEditingAppointmentType(type);
    setAppointmentTypeDialogOpen(true);
  };

  useEffect(() => {
    fetchAppointmentTypes();
  }, []);
  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='flex items-center gap-2'>
              <ListTodo className='h-5 w-5 text-teal-600' />
              Appointment Types
            </CardTitle>
            <CardDescription>Define the types of appointments patients can book</CardDescription>
          </div>
          <Button className='bg-teal-600 hover:bg-teal-700' onClick={handleCreateAppointmentType}>
            <Plus className='mr-2 h-4 w-4' />
            New Type
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className='py-12 text-center text-slate-500'>
            <Loader2 className='mx-auto mb-4 h-12 w-12 animate-spin' />
            <p>Loading appointment types...</p>
          </div>
        ) : appointmentTypes.length === 0 ? (
          <div className='py-12 text-center text-slate-500'>
            <ListTodo className='mx-auto mb-4 h-12 w-12 opacity-50' />
            <p>No appointment types configured</p>
            <p className='text-sm'>Create your first appointment type to get started</p>
            <Button
              className='mt-4 bg-teal-600 hover:bg-teal-700'
              onClick={handleCreateAppointmentType}
            >
              <Plus className='mr-2 h-4 w-4' />
              Create Appointment Type
            </Button>
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
            {appointmentTypes.map(type => (
              <AppointmentTypeCard
                key={type.id}
                id={type.id}
                name={type.name}
                description={type.description}
                durationMinutes={type.duration_minutes}
                price={type.price}
                priceCents={type.price_cents}
                color={type.color}
                telehealthEnabled={type.telehealth_enabled}
                inPersonEnabled={type.in_person_enabled}
                isActive={type.is_active}
                isPublic={type.is_public}
                onEdit={() => handleEditAppointmentType(type)}
                onSuccess={fetchAppointmentTypes}
                bookingUrl={v2UrlByType[type.id]}
              />
            ))}
          </div>
        )}
      </CardContent>
      <AppointmentTypeDialog
        open={appointmentTypeDialogOpen}
        onOpenChange={setAppointmentTypeDialogOpen}
        isEditing={editingAppointmentType !== null}
        appointmentType={editingAppointmentType}
        onSuccess={() => {
          setEditingAppointmentType(null);
          fetchAppointmentTypes();
        }}
      />
    </Card>
  );
};

export default AppointmentTypeTab;
